// Il «quadro strumenti» del cruscotto: lancette e spie, come sul cruscotto di un'auto.
import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Icona } from '../layout/Marchio'
import { ICONE, type NomeIcona } from '../layout/icone'
import type { Tono } from './quadro'

const COLORE_ARCO: Record<Tono, string> = { rosso: '#ef4444', ambra: '#f59e0b', verde: '#22c55e', grigio: '#334155' }

// Semicerchio di raggio 80 centrato in (100, 100): frazione 0 a sinistra, 1 a destra.
const punto = (frazione: number, raggio = 80) => {
  const angolo = Math.PI * (1 - frazione)
  return { x: 100 + raggio * Math.cos(angolo), y: 100 - raggio * Math.sin(angolo) }
}
const arco = (da: number, a: number) => {
  const p = punto(da)
  const q = punto(a)
  return `M${p.x.toFixed(2)} ${p.y.toFixed(2)} A80 80 0 0 1 ${q.x.toFixed(2)} ${q.y.toFixed(2)}`
}

export interface Zona {
  /** Fine della zona, nella stessa unita' di `min` e `max`. */
  readonly fino: number
  readonly tono: Tono
}

export function Lancetta(props: {
  valore: number | null
  min: number
  max: number
  zone: readonly Zona[]
  testo: string
  etichetta: string
  dettaglio: ReactNode
}) {
  const { valore, min, max, zone, testo, etichetta, dettaglio } = props
  const frazione = (v: number) => Math.min(1, Math.max(0, (v - min) / (max - min)))
  // Il numero prende il colore della zona su cui sta la lancetta: niente letture contraddittorie.
  const tono = valore === null ? 'grigio' : (zone.find((z) => valore <= z.fino) ?? zone[zone.length - 1]!).tono
  const ago = valore === null ? null : punto(frazione(valore), 64)
  return (
    <div className="lancetta">
      <svg width="220" height="128" viewBox="0 0 200 116" aria-hidden="true">
        <path d={arco(0, 1)} fill="none" stroke="#1e293b" strokeWidth="16" />
        {zone.map((z, i) => (
          <path key={i} d={arco(frazione(i === 0 ? min : zone[i - 1]!.fino), frazione(z.fino))} fill="none" stroke={COLORE_ARCO[z.tono]} strokeWidth="16" />
        ))}
        {ago && <line x1="100" y1="100" x2={ago.x} y2={ago.y} stroke="#f8fafc" strokeWidth="4" strokeLinecap="round" />}
        <circle cx="100" cy="100" r="7" fill="#f8fafc" />
      </svg>
      <span className={`lancetta-valore tono-${tono}`}>{testo}</span>
      <span className="lancetta-etichetta">{etichetta}</span>
      <span className="lancetta-dettaglio">{dettaglio}</span>
    </div>
  )
}

/** `tono` nullo = spia spenta: va tutto bene. */
export function Spia(props: { tono: Tono | null; icona: NomeIcona; valore: ReactNode; etichetta: string; a?: string }) {
  const { tono, icona, valore, etichetta, a } = props
  const contenuto = (
    <>
      <span className={tono ? `spia-luce accesa-${tono}` : 'spia-luce'}>
        <Icona d={ICONE[icona]} dimensione={30} />
      </span>
      <span className={typeof valore === 'string' && valore.length > 6 ? 'spia-valore testo' : 'spia-valore'}>{valore}</span>
      <span className="spia-etichetta">{etichetta}</span>
    </>
  )
  return a ? <Link to={a} className="spia">{contenuto}</Link> : <div className="spia">{contenuto}</div>
}

export function Stato({ tono, children }: { tono: Tono; children: ReactNode }) {
  return <span className={`stato stato-${tono}`}>{children}</span>
}

/** Scala dei giorni residui: rosso nelle ultime due settimane, ambra fino a 45 giorni. */
export const ZONE_GIORNI: readonly Zona[] = [
  { fino: 15, tono: 'rosso' },
  { fino: 45, tono: 'ambra' },
  { fino: 120, tono: 'verde' },
]
